// ============================================================================
// Hook: useOrdenacaoTabela
// Objetivo: dar ordenação clicável (por cabeçalho) às tabelas do portal sem
//           repetir a lógica em cada tela. Nasceu no Financeiro (Estorno, Petty
//           Cash, Troco, Relatório de Fechamento) e hoje também é usado pelo
//           Fiscal e pelo Treinamento/PPI — as tabelas são <table> com estilo
//           inline, sem componente compartilhado, então a lógica comum mora aqui.
//           (Mora na pasta do Financeiro por herança; é código compartilhado.)
//
// A "pegadinha" que este hook resolve: ordenar texto puro quebra dois casos
// comuns nessas telas — DATAS em dd/mm/aaaa (alfabeticamente "02/01/2026" viria
// ANTES de "28/12/2025", o que é errado) e VALORES em R$ ("R$ 9" viria depois
// de "R$ 80"). Por isso o comparador trata cada coluna pelo seu tipo.
// ============================================================================

import { useState, useMemo } from 'react';

// --- Conversores auxiliares -------------------------------------------------

// Tenta ler uma data brasileira "dd/mm/aaaa" (com ou sem hora depois) e devolve
// o timestamp para comparação cronológica. Devolve null se o texto não for data.
const paraData = (v) => {
    if (v === null || v === undefined) return null;
    const m = String(v).trim().match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    if (!m) return null;
    const [, dd, mm, aaaa] = m;
    return new Date(Number(aaaa), Number(mm) - 1, Number(dd)).getTime();
};

// Tenta ler um número, tolerando string. Devolve null se não for número válido.
// (Os campos de valor dessas tabelas chegam como número ou string numérica
// "123.45", então um Number() simples já resolve.)
const paraNumero = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
};

const ehVazio = (v) => v === null || v === undefined || v === '';

// Compara dois valores conforme o tipo da coluna. Valores vazios sempre por
// último (tratados fora daqui, antes de chegar no comparador de tipo).
const compararPorTipo = (a, b, tipo) => {
    if (tipo === 'data') {
        // Se um dos lados não for data válida, cai para comparação textual.
        const da = paraData(a);
        const db = paraData(b);
        if (da !== null && db !== null) return da - db;
    } else if (tipo === 'numero') {
        const na = paraNumero(a);
        const nb = paraNumero(b);
        if (na !== null && nb !== null) return na - nb;
    } else if (tipo === 'auto') {
        // Usado quando não sabemos o tipo da coluna (ex.: Relatório de
        // Fechamento, com colunas dinâmicas vindas da procedure). Tenta data,
        // depois número, e só então texto.
        const da = paraData(a);
        const db = paraData(b);
        if (da !== null && db !== null) return da - db;
        const na = paraNumero(a);
        const nb = paraNumero(b);
        if (na !== null && nb !== null) return na - nb;
    }
    // 'texto' (padrão) — numeric:true faz "Loja 2" < "Loja 10" corretamente.
    return String(a).localeCompare(String(b), 'pt-BR', { numeric: true, sensitivity: 'base' });
};

// --- Hook -------------------------------------------------------------------

// dados : array a ser ordenado (ex.: dadosEstornos, transacoesFiltradas...)
// tipos : mapa { NOME_DO_CAMPO: 'data' | 'numero' | 'texto' }. Campo ausente
//         no mapa é tratado como 'auto'. IMPORTANTE: declare esse mapa FORA do
//         componente (const de módulo) para a identidade ser estável e o useMemo
//         não re-ordenar a cada render.
// opcoes: { corAtiva } — cor da seta da coluna ordenada. O padrão é o teal do
//         Financeiro (onde o hook nasceu); cada módulo passa a SUA cor de acento
//         para a seta não destoar do resto da tela.
const useOrdenacaoTabela = (dados, tipos = {}, { corAtiva = '#0f766e' } = {}) => {
    // null = sem ordenação (mantém a ordem que a API/filtro entregou).
    const [ordenacao, setOrdenacao] = useState(null); // { campo, direcao: 'asc'|'desc' }

    // Alterna ao clicar no cabeçalho: 1º clique = crescente; 2º clique na mesma
    // coluna = decrescente; clicar em outra coluna recomeça em crescente.
    const ordenar = (campo) => {
        setOrdenacao((prev) =>
            prev && prev.campo === campo && prev.direcao === 'asc'
                ? { campo, direcao: 'desc' }
                : { campo, direcao: 'asc' }
        );
    };

    // Volta à ordem em que a API entregou. Existe para telas cuja ordem NATURAL
    // carrega significado — o Controle do Troco é um relatório, e a ordem
    // cronológica é o que faz a lista contar a história do dinheiro. Sem isto,
    // depois do primeiro clique não haveria como voltar a ela senão recarregando.
    const limparOrdenacao = () => setOrdenacao(null);

    const dadosOrdenados = useMemo(() => {
        if (!ordenacao) return dados;
        const { campo, direcao } = ordenacao;
        const tipo = tipos[campo] || 'auto';
        const fator = direcao === 'asc' ? 1 : -1;

        // Cópia para nunca mutar o array original (sort() ordena no lugar).
        return [...dados].sort((a, b) => {
            const va = a[campo];
            const vb = b[campo];
            // Vazios sempre no fim, independente da direção.
            if (ehVazio(va) && ehVazio(vb)) return 0;
            if (ehVazio(va)) return 1;
            if (ehVazio(vb)) return -1;
            return fator * compararPorTipo(va, vb, tipo);
        });
    }, [dados, ordenacao, tipos]);

    // Indicador visual para o cabeçalho. Mostra SEMPRE um símbolo, mesmo quando a
    // coluna não está ordenada — assim fica claro que toda coluna com esse ícone é
    // clicável/ordenável (a queixa de "não ficou didático" era justamente que a
    // seta só aparecia depois de clicar):
    //   - coluna inativa  → ⇅ apagadinho (cinza) = "dá para ordenar por aqui"
    //   - coluna ativa    → ▲ (crescente) ou ▼ (decrescente) na cor de acento
    const indicador = (campo) => {
        const ativo = ordenacao && ordenacao.campo === campo;
        const simbolo = !ativo ? '⇅' : ordenacao.direcao === 'asc' ? '▲' : '▼';
        return (
            <span style={{
                marginLeft: 4,
                fontSize: '0.9em',
                color: ativo ? corAtiva : '#cbd5e1',
                fontWeight: ativo ? 700 : 400,
            }}>{simbolo}</span>
        );
    };

    return { dadosOrdenados, ordenar, indicador, ordenacao, limparOrdenacao };
};

export default useOrdenacaoTabela;
